import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/app/components/ui/Alert';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { StatTile } from '@/app/components/ui/StatTile';
import { cn } from '@/lib/cn';
import { formatDate, formatNumber, formatShortDate } from '@/lib/format';
import { seriesStroke, useThemeColors } from '@/lib/useThemeColors';
import { useAsync } from '@/lib/useAsync';
import type {
  AvailabilityRow,
  DashboardPanelResult,
  DashboardPanelSpec,
  SeriesMetric,
  SessionRow,
} from '@/features/statistics/api';
import { runDashboardBatch } from '@/features/statistics/api';
import { panelTitle } from '@/features/statistics/panelTitle';

function formatValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return Number.isInteger(value)
    ? formatNumber(value)
    : formatNumber(value, { maximumFractionDigits: 2 });
}

/**
 * One statistics element: `{ target, type, range, grain, metrics }` in, a
 * `Card` with a title and a chart/table/tiles out — including its own load,
 * 403 and `insufficient_data` states. Fetches on its own via the batch
 * endpoint (with a single panel) unless a `result` is supplied directly
 * (tests, the styleguide's fixture data, or a caller that already ran a
 * batch itself — see `StatsGrid`).
 */
export function StatsPanel({
  spec,
  result,
  title,
  detailTo,
  className,
}: {
  spec: DashboardPanelSpec;
  /** Skip the panel's own fetch and render this result instead. */
  result?: DashboardPanelResult;
  /** Overrides the derived title (`panelTitle(spec)`). */
  title?: string;
  /** "View details" link to the full chart/table for this panel. */
  detailTo?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const shouldFetch = result === undefined;
  const batch = useAsync(
    () => (shouldFetch ? runDashboardBatch([spec]) : Promise.resolve(null)),
    [shouldFetch, JSON.stringify(spec)],
  );

  const resolved = result ?? (batch.data ? batch.data.panels[spec.id] : undefined);
  const loading = shouldFetch && batch.loading;

  return (
    <Card className={cn(className)}>
      <CardHeader
        title={title ?? panelTitle(spec, t)}
        action={
          detailTo ? (
            <Link to={detailTo} className="text-accent text-sm">
              {t('statistics.detailLink')}
            </Link>
          ) : undefined
        }
      />
      {loading ? (
        <Skeleton kind="card" />
      ) : shouldFetch && batch.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: batch.error.message })}
        </Alert>
      ) : (
        <PanelOutcome spec={spec} result={resolved} />
      )}
    </Card>
  );
}

/** Renders one panel's outcome once a result is in hand — shared by
 * `StatsPanel` (self-fetching) and `StatsGrid` (one batch call for many). */
export function PanelOutcome({
  spec,
  result,
}: {
  spec: DashboardPanelSpec;
  result: DashboardPanelResult | undefined;
}) {
  if (!result) {
    return <Skeleton kind="card" />;
  }

  if (result.status !== 'ok') {
    return <StatusNote status={result.status} />;
  }

  return <PanelBody spec={spec} result={result} />;
}

export function StatusNote({ status }: { status: 'forbidden' | 'insufficient_data' }) {
  const { t } = useTranslation();
  if (status === 'forbidden') {
    return (
      <Alert tone="danger" role="alert">
        {t('statistics.status.forbidden')}
      </Alert>
    );
  }
  return (
    <Alert tone="info" role="status">
      {t('statistics.status.insufficientData')}
    </Alert>
  );
}

function PanelBody({ spec, result }: { spec: DashboardPanelSpec; result: DashboardPanelResult }) {
  if (spec.type === 'series')
    return <SeriesPanel series={result.series ?? []} grain={spec.grain} />;
  if (spec.type === 'sessions') return <SessionsPanel sessions={result.sessions ?? []} />;
  return <AvailabilityPanel rows={result.available ?? []} />;
}

/**
 * `grain: range` collapses to one totals bucket per metric — a number, not a
 * trend — so it renders as stat tiles rather than a one-point line chart.
 */
function SeriesPanel({
  series,
  grain,
}: {
  series: SeriesMetric[];
  grain?: DashboardPanelSpec['grain'];
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();

  if (series.length === 0) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noMetrics')}
      </Alert>
    );
  }

  if (grain === 'range') {
    return (
      <div className="flex flex-wrap gap-4">
        {series.map((metric) => (
          <StatTile
            key={metric.metric_key}
            value={formatValue(metric.buckets[0]?.value)}
            label={`${metric.metric_key} (${metric.unit})`}
          />
        ))}
      </div>
    );
  }

  const rows = new Map<string, Record<string, number | string | null>>();
  for (const metric of series) {
    for (const bucket of metric.buckets) {
      const row = rows.get(bucket.bucket) ?? { bucket: bucket.bucket };
      row[metric.metric_key] = bucket.value;
      rows.set(bucket.bucket, row);
    }
  }
  const data = Array.from(rows.values()).sort((a, b) =>
    String(a.bucket).localeCompare(String(b.bucket)),
  );

  if (data.length === 0) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noData')}
      </Alert>
    );
  }

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid stroke={`${colors.ink}1a`} />
          <XAxis
            dataKey="bucket"
            tick={{ fontSize: 11, fill: colors.ink }}
            tickFormatter={(value: unknown) => formatShortDate(String(value))}
            minTickGap={24}
          />
          <YAxis tick={{ fontSize: 11, fill: colors.ink }} />
          <Tooltip
            contentStyle={{
              background: 'var(--paper)',
              border: '1px solid var(--ink)',
              borderRadius: 12,
            }}
            labelFormatter={(value: unknown) => formatDate(String(value))}
          />
          {series.length > 1 && <Legend />}
          {series.map((metric, index) => {
            const { stroke, dashed } = seriesStroke(colors, index, metric.metric_key);
            return (
              <Line
                key={metric.metric_key}
                type="monotone"
                dataKey={metric.metric_key}
                stroke={stroke}
                strokeWidth={2}
                {...(dashed ? { strokeDasharray: '4 3' } : {})}
                dot={false}
                connectNulls
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function SessionsPanel({ sessions }: { sessions: SessionRow[] }) {
  const { t } = useTranslation();

  if (sessions.length === 0) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noSessions')}
      </Alert>
    );
  }

  return (
    <DataTable
      rows={sessions}
      rowKey={(session) => session.measurement_id}
      columns={[
        {
          key: 'date',
          header: t('statistics.table.date'),
          render: (s) => formatDate(s.local_date),
        },
        {
          key: 'type',
          header: t('statistics.table.type'),
          render: (s) => (s.subtype ? `${s.session_type} (${s.subtype})` : s.session_type),
        },
        {
          key: 'duration',
          header: t('statistics.table.duration'),
          render: (s) => t('statistics.table.minutes', { count: Math.round(s.duration_s / 60) }),
        },
        {
          key: 'calories',
          header: t('statistics.table.calories'),
          align: 'end',
          render: (s) =>
            s.calories_kcal !== null ? formatNumber(Math.round(s.calories_kcal)) : '—',
        },
        {
          key: 'hr',
          header: t('statistics.table.avgHeartRate'),
          align: 'end',
          render: (s) => s.hr_avg ?? '—',
        },
      ]}
    />
  );
}

function AvailabilityPanel({ rows }: { rows: AvailabilityRow[] }) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noAvailability')}
      </Alert>
    );
  }

  return (
    <DataTable
      rows={rows.map((row, index) => ({ ...row, _key: String(index) }))}
      rowKey={(row) => row._key}
      columns={[
        { key: 'metric', header: t('statistics.table.metric'), render: (r) => r.metric_key },
        { key: 'source', header: t('statistics.table.source'), render: (r) => r.source_id },
        {
          key: 'first',
          header: t('statistics.table.first'),
          render: (r) => formatDate(r.first_date),
        },
        { key: 'last', header: t('statistics.table.last'), render: (r) => formatDate(r.last_date) },
        {
          key: 'days',
          header: t('statistics.table.days'),
          align: 'end',
          render: (r) => r.day_count,
        },
      ]}
    />
  );
}
